import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { renderAsync } from "docx-preview";
import { ExternalLink, FileDown, FileText, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import PdfViewer from "@/components/PdfViewer";
import {
  getFileExtension,
  getMicrosoftOfficeEmbedUrl,
  isOfficeDocumentFile,
  isPdfFile,
} from "@/lib/modalities";
import { publicFetchBlob } from "@/lib/shareApi";
import { fetchFileSignedUrl, isPublicHttpsFileUrl, canEmbedOfficeViewerInPage } from "@/lib/fileSignedUrl";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PPT_EXTENSIONS = new Set([".ppt", ".pptx"]);

const OfficeDocumentViewerModal = ({
  open,
  onOpenChange,
  file,
  previewUrl,
  serveUrl,
}) => {
  const previewRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [embedUrl, setEmbedUrl] = useState(null);
  const [officeViewUrl, setOfficeViewUrl] = useState(null);
  const [needsDownload, setNeedsDownload] = useState(false);

  const fileName = file?.original_filename || file?.filename || "Document";
  const fileId = file?.id;
  const extension = getFileExtension(fileName);
  const isPdf = isPdfFile(fileName) || file?.file_type === "pdf";
  const isPpt = PPT_EXTENSIONS.has(extension);
  const isDocx = extension === ".docx";
  const isLegacyDoc = extension === ".doc";
  const downloadHref = serveUrl || (fileId ? `${API}/files/${fileId}/serve` : null);

  const handleOpenOfficePreview = () => {
    if (!officeViewUrl) return;
    window.open(officeViewUrl, "_blank", "noopener,noreferrer");
  };

  const handleDownload = async () => {
    if (!downloadHref) return;
    try {
      const blob = previewUrl
        ? await publicFetchBlob(downloadHref)
        : (await axios.get(downloadHref, { responseType: "blob" })).data;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setError("Download failed");
    }
  };

  const previewFallback = (
    <div className="flex flex-col items-center justify-center h-full min-h-[50vh] gap-4 px-4 text-center max-w-md mx-auto">
      <div className="w-14 h-14 rounded-2xl bg-[var(--xl-primary-soft)] flex items-center justify-center">
        <FileText className="w-7 h-7 text-[var(--xl-primary)]" />
      </div>
      <p className="text-sm text-muted-foreground">
        {error
          || (officeViewUrl
            ? "Microsoft cannot embed this preview on http://localhost. Open it in a new tab, or use HTTPS in deployed XLSync."
            : "Download the file to open it locally.")}
      </p>
      <div className="flex flex-wrap items-center justify-center gap-2">
        {officeViewUrl ? (
          <Button type="button" className="gap-2" onClick={handleOpenOfficePreview}>
            <ExternalLink className="w-4 h-4" />
            Open preview
          </Button>
        ) : null}
        {downloadHref ? (
          <Button
            type="button"
            variant={officeViewUrl ? "outline" : "default"}
            className="gap-2"
            onClick={handleDownload}
          >
            <FileDown className="w-4 h-4" />
            Download
          </Button>
        ) : null}
      </div>
    </div>
  );

  useEffect(() => {
    if (!open || !file || isPdf) return undefined;

    let cancelled = false;
    setLoading(true);
    setError(null);
    setEmbedUrl(null);
    setOfficeViewUrl(null);
    setNeedsDownload(false);

    const loadPreview = async () => {
      try {
        if (isPpt || isLegacyDoc) {
          const publicFileUrl = previewUrl || (fileId ? await fetchFileSignedUrl(fileId) : null);
          if (!isPublicHttpsFileUrl(publicFileUrl)) {
            if (!cancelled) {
              setNeedsDownload(true);
              setError(
                publicFileUrl
                  ? "Preview needs a public HTTPS file URL (S3). This signed link is not reachable by Microsoft (localhost or HTTP)."
                  : "Could not get a preview link. Download the file to open it locally.",
              );
            }
            return;
          }
          const officeUrl = getMicrosoftOfficeEmbedUrl(publicFileUrl);
          if (!cancelled) {
            setOfficeViewUrl(officeUrl);
            if (canEmbedOfficeViewerInPage()) {
              setEmbedUrl(officeUrl);
            }
          }
          return;
        }

        if (isDocx) {
          if (!fileId && !previewUrl) {
            throw new Error("Missing file id");
          }
          const blob = previewUrl
            ? await publicFetchBlob(previewUrl)
            : (await axios.get(`${API}/files/${fileId}/serve`, { responseType: "blob" })).data;
          if (cancelled) return;
          // Container stays mounted under the spinner; wait one frame if React has not committed yet.
          if (!previewRef.current) {
            await new Promise((resolve) => requestAnimationFrame(resolve));
          }
          if (cancelled || !previewRef.current) {
            throw new Error("Preview container not ready");
          }
          previewRef.current.innerHTML = "";
          await renderAsync(blob, previewRef.current, null, {
            className: "docx-preview",
            inWrapper: true,
            ignoreWidth: false,
            ignoreHeight: false,
          });
          return;
        }

        if (!isOfficeDocumentFile(fileName)) {
          throw new Error("Unsupported document type");
        }
      } catch {
        if (!cancelled) setError("Could not load document preview");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadPreview();

    return () => {
      cancelled = true;
      if (previewRef.current) previewRef.current.innerHTML = "";
    };
  }, [open, file, fileId, fileName, isPdf, isPpt, isDocx, isLegacyDoc, previewUrl, downloadHref]);

  if (!file) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[min(96vw,72rem)] w-full h-[min(92vh,52rem)] p-0 gap-0 overflow-hidden flex flex-col"
        data-testid="office-document-viewer-modal"
      >
        <DialogHeader className="px-4 py-3 border-b shrink-0">
          <DialogTitle className="text-sm font-medium truncate pr-8">{fileName}</DialogTitle>
        </DialogHeader>

        <div className="relative flex-1 min-h-0 bg-[var(--xl-bg)]">
          {isPdf ? (
            <PdfViewer fileId={fileId} fileName={fileName} serveUrl={serveUrl} />
          ) : embedUrl ? (
            <iframe
              title={fileName}
              src={embedUrl}
              className="w-full h-full min-h-[50vh] border-0 bg-white"
              data-testid="office-document-embed"
            />
          ) : officeViewUrl || error || needsDownload ? (
            previewFallback
          ) : (
            <>
              {loading ? (
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-white">
                  <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                </div>
              ) : null}
              <div
                ref={previewRef}
                className="h-full min-h-[50vh] overflow-auto bg-white px-4 sm:px-8 py-6"
                data-testid="docx-preview-container"
              />
            </>
          )}
        </div>

        {!isPdf && downloadHref && !needsDownload ? (
          <div className="border-t px-4 py-2.5 flex justify-end shrink-0 bg-white">
            <Button type="button" variant="outline" size="sm" className="gap-2" onClick={handleDownload}>
              <FileDown className="w-4 h-4" />
              Download
            </Button>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default OfficeDocumentViewerModal;
